import { Button, Icon, Input, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { type FC, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { ProductFormValues } from "./product-form.types";

interface GrossCommissionFieldProps {
  canEdit: boolean;
}

export const GrossCommissionField: FC<GrossCommissionFieldProps> = ({ canEdit }) => {
  const { setValue } = useFormContext<ProductFormValues>();
  const grossCommission = useWatch<ProductFormValues, "grossCommission">({ name: "grossCommission" });
  const marketType = useWatch<ProductFormValues, "marketType">({ name: "marketType" });
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Calculate estimated QW spread
  const defaultAgentCommission = marketType === "brokerage" ? 10 : 12.5;
  const estimatedSpread = grossCommission != null ? grossCommission - defaultAgentCommission : null;

  const hasCommission = grossCommission != null;
  const showInput = !hasCommission || isEditing;

  const handleSave = () => {
    const value = parseFloat(inputValue);
    if (!Number.isNaN(value) && value >= 0 && value <= 99.99) {
      setValue("grossCommission", value, { shouldDirty: true });
      setIsEditing(false);
      setInputValue("");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue("");
  };

  const handleEdit = () => {
    setInputValue(grossCommission?.toString() ?? "");
    setIsEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div className="flex flex-row gap-4">
      <div className="space-y-2">
        <label className="text-sm text-muted-foreground font-semibold">Gross Commission</label>
        {showInput && canEdit ? (
          <div className="flex items-center gap-2">
            <div className="relative w-24">
              <Input
                type="number"
                min={0}
                max={99.99}
                step={0.1}
                placeholder="15"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pr-6"
                autoFocus={isEditing}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleSave} className="h-8 w-8 p-0">
              <Icon icon="check" className="h-4 w-4" />
            </Button>
            {isEditing && (
              <Button type="button" variant="ghost" size="sm" onClick={handleCancel} className="h-8 w-8 p-0 border">
                <Icon icon="close" className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <p className="text-sm">{grossCommission != null ? `${grossCommission.toFixed(1)}%` : "Not set"}</p>
            {canEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="sm" onClick={handleEdit} className="h-6 w-6 p-0">
                    <Icon icon="edit" className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit default commission</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {hasCommission && (
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground font-semibold">Est. QW Spread</label>
          <p className="text-sm">{estimatedSpread != null ? `${estimatedSpread.toFixed(1)}%` : "Not set"}</p>
        </div>
      )}
    </div>
  );
};
